import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  User as FirebaseUser,
  onAuthStateChanged
} from 'firebase/auth';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role?: 'builder' | 'sponsor';
  hasCompletedOnboarding: boolean;
  isRegistered: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  hasCompletedOnboarding: boolean;
  isRegistered: boolean;
  registerUser: (userData: {
    role: 'builder' | 'sponsor';
    firstName?: string;
    lastName?: string;
    username?: string;
    location?: string;
    skills?: string[];
    bio?: string;
    socialLinks?: { platform: string; url: string; }[];
    profilePic?: string | null;
    companyName?: string;
    companyUrl?: string;
    companyDescription?: string;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock function to simulate backend check
const checkUserRegistration = async (uid: string): Promise<boolean> => {
  // For testing: Always return false to simulate unregistered user
  return false;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'No user');
      
      try {
        if (firebaseUser) {
          // Check if user is registered
          const isRegistered = await checkUserRegistration(firebaseUser.uid);
          console.log('User registration status:', isRegistered);
          console.log('Firebase user data:', {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            providerData: firebaseUser.providerData
          });

          const user: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            hasCompletedOnboarding: false,
            role: undefined,
            isRegistered: isRegistered
          };

          console.log('Setting user data:', user);
          setUser(user);
        } else {
          console.log('No user, setting user to null');
          setUser(null);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      console.log('Cleaning up auth state listener...');
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google sign in...');
      const provider = new GoogleAuthProvider();
      // Request additional scopes for profile information
      provider.addScope('profile');
      provider.addScope('email');
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign in successful', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });
      
      // After successful sign in, check registration status
      const isRegistered = await checkUserRegistration(result.user.uid);
      console.log('User registration status after sign in:', isRegistered);
      
      // Update user state with registration status and profile info
      setUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        hasCompletedOnboarding: false,
        isRegistered: isRegistered
      });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const registerUser = async (userData: {
    role: 'builder' | 'sponsor';
    firstName?: string;
    lastName?: string;
    username?: string;
    location?: string;
    skills?: string[];
    bio?: string;
    socialLinks?: { platform: string; url: string; }[];
    profilePic?: string | null;
    companyName?: string;
    companyUrl?: string;
    companyDescription?: string;
  }) => {
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      console.log('Registering user with data:', userData);
      
      const endpoint = userData.role === 'builder' 
        ? 'YOUR_BACKEND_API_ENDPOINT/users/builder'
        : 'YOUR_BACKEND_API_ENDPOINT/users/sponsor';

      // Create registration data with email
      const registrationData = {
        uid: user.uid,
        email: user.email, // Explicitly include email
        displayName: user.displayName,
        photoURL: user.photoURL,
        hasCompletedOnboarding: true,
        isRegistered: true,
        ...userData
      };

      console.log('Sending registration data:', registrationData);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      if (!response.ok) {
        throw new Error('Failed to register user');
      }

      // Update local user state
      setUser(prevUser => {
        if (prevUser) {
          return {
            ...prevUser,
            role: userData.role,
            hasCompletedOnboarding: true,
            isRegistered: true
          };
        }
        return null;
      });

      console.log('User registration successful');
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out...');
      await firebaseSignOut(auth);
      console.log('Sign out successful');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    hasCompletedOnboarding: user?.hasCompletedOnboarding || false,
    isRegistered: user?.isRegistered || false,
    registerUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 